"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowUp,
  FileText,
  Heading,
  Image as ImageIcon,
  Paperclip,
  Sparkles,
  TextQuote,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { GrowthFeatureCard } from "./GrowthFeatureCard";
import {
  DEFAULT_GROWTH_FEATURE_AI_CONTENT_COPY,
  type GrowthFeatureAiContentCardCopy,
  type GrowthFeatureAiContentToolId,
} from "./growth-feature-ai-content-copy";

const AI_CONTENT_TOOL_ICONS: Record<GrowthFeatureAiContentToolId, LucideIcon> = {
  title: Heading,
  meta: TextQuote,
  brief: FileText,
};

const TYPE_CHAR_MS = 38;
const PAUSE_AFTER_TYPE_MS = 1400;

function useAutotype(text: string, enabled: boolean) {
  const [displayedText, setDisplayedText] = useState(enabled ? "" : text);

  useEffect(() => {
    if (!enabled) {
      setDisplayedText(text);
      return;
    }

    setDisplayedText("");
    let charIndex = 0;
    let timeoutId = 0;

    const typeNextChar = () => {
      charIndex += 1;
      setDisplayedText(text.slice(0, charIndex));

      if (charIndex < text.length) {
        timeoutId = window.setTimeout(typeNextChar, TYPE_CHAR_MS);
      }
    };

    timeoutId = window.setTimeout(typeNextChar, TYPE_CHAR_MS);

    return () => window.clearTimeout(timeoutId);
  }, [enabled, text]);

  return displayedText;
}

type GrowthFeatureAiContentProps = {
  copy?: GrowthFeatureAiContentCardCopy;
};

export function GrowthFeatureAiContent({
  copy = DEFAULT_GROWTH_FEATURE_AI_CONTENT_COPY,
}: GrowthFeatureAiContentProps = {}) {
  const tools = useMemo(
    () =>
      copy.demo.tools.map((tool) => ({
        ...tool,
        icon: AI_CONTENT_TOOL_ICONS[tool.id],
      })),
    [copy.demo.tools],
  );

  const [activeToolIndex, setActiveToolIndex] = useState(0);
  const [isSendPressed, setIsSendPressed] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const activeTool = tools[activeToolIndex] ?? tools[0];
  const displayedPrompt = useAutotype(
    activeTool?.prompt ?? "",
    !prefersReducedMotion,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setPrefersReducedMotion(mediaQuery.matches);

    syncPreference();
    mediaQuery.addEventListener("change", syncPreference);
    return () => mediaQuery.removeEventListener("change", syncPreference);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || !activeTool) {
      return;
    }

    const typingDuration = activeTool.prompt.length * TYPE_CHAR_MS;
    const timeoutId = window.setTimeout(() => {
      setActiveToolIndex((current) => (current + 1) % tools.length);
    }, typingDuration + PAUSE_AFTER_TYPE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [activeTool, activeToolIndex, prefersReducedMotion, tools.length]);

  const handleToolSelect = useCallback((index: number) => {
    setActiveToolIndex(index);
  }, []);

  const handleSendPointerDown = useCallback(() => {
    setIsSendPressed(true);
  }, []);

  const handleSendPointerUp = useCallback(() => {
    setIsSendPressed(false);
  }, []);

  return (
    <GrowthFeatureCard
      className="cc-growth-features__card--ai-content"
      title={copy.title}
      badge={copy.badge}
      icon={Sparkles}
      description={copy.description}
    >
      <div className="cc-growth-features__ai-content" aria-hidden="true">
        <div className="cc-growth-features__ai-content-panel">
          <div className="cc-growth-features__ai-content-shell">
            <div className="cc-growth-features__ai-content-body">
              <div className="cc-growth-features__ai-content-prompt">
                <p className="cc-growth-features__ai-content-placeholder">
                  <span className="cc-growth-features__ai-content-prompt-text">
                    {displayedPrompt}
                  </span>
                  {!prefersReducedMotion ? (
                    <span className="cc-growth-features__ai-content-cursor">|</span>
                  ) : null}
                </p>
                <div className="cc-growth-features__ai-content-actions">
                  <div className="cc-growth-features__ai-content-attach">
                    <button
                      type="button"
                      className="cc-growth-features__ai-content-action"
                      data-cc-cursor-ignore
                      tabIndex={-1}
                    >
                      <Paperclip
                        className="cc-growth-features__ai-content-icon"
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      type="button"
                      className="cc-growth-features__ai-content-action"
                      data-cc-cursor-ignore
                      tabIndex={-1}
                    >
                      <ImageIcon
                        className="cc-growth-features__ai-content-icon"
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    className={
                      isSendPressed
                        ? "cc-growth-features__ai-content-send cc-growth-features__ai-content-send--pressed"
                        : "cc-growth-features__ai-content-send"
                    }
                    data-cc-cursor-ignore
                    tabIndex={-1}
                    onPointerDown={handleSendPointerDown}
                    onPointerUp={handleSendPointerUp}
                    onPointerLeave={handleSendPointerUp}
                    onPointerCancel={handleSendPointerUp}
                  >
                    <ArrowUp
                      className="cc-growth-features__ai-content-send-icon"
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
              <div className="cc-growth-features__ai-content-tools">
                {tools.map((tool, index) => {
                  const ToolIcon = tool.icon;

                  return (
                    <button
                      key={tool.id}
                      type="button"
                      className={
                        index === activeToolIndex
                          ? "cc-growth-features__ai-content-tool cc-growth-features__ai-content-tool--active"
                          : "cc-growth-features__ai-content-tool"
                      }
                      tabIndex={-1}
                      onClick={() => handleToolSelect(index)}
                    >
                      <ToolIcon
                        className="cc-growth-features__ai-content-tool-icon"
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                      <span className="cc-growth-features__ai-content-tool-label">
                        {tool.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </GrowthFeatureCard>
  );
}
